import { useCallback, useEffect } from 'react'
import { usePageControl } from './page-control.hook.js'

/** Keys a page-turner (BT pedal or keyboard) emits to go forward / backward. */
export const PAGE_TURN_FORWARD_KEYS = ['PageDown', 'ArrowRight', 'ArrowDown'] as const
export const PAGE_TURN_BACKWARD_KEYS = ['PageUp', 'ArrowLeft', 'ArrowUp'] as const

export const isPageTurnKey = (key: string): boolean =>
  (PAGE_TURN_FORWARD_KEYS as readonly string[]).includes(key) ||
  (PAGE_TURN_BACKWARD_KEYS as readonly string[]).includes(key)

/** The keys a pedal sends. A control that passes the pedals through leaves these to the page turner. */
const PEDAL_KEYS = ['PageUp', 'PageDown'] as const

const pedalPassThroughAttribute = 'data-pedal-pass-through'

/**
 * Spread on a form control that leaves PageUp/PageDown to the page-turner pedals, such as the seek slider in
 * performance mode: the pedals turn the page even while it has focus, and its arrow keys stay its own.
 */
export const pedalPassThroughProps = { [pedalPassThroughAttribute]: 'true' } as const

const formControlSelector = 'input, select, textarea, [role="slider"]'

/**
 * The focused element owns this key, so a page turn must leave it alone: editable text, or a form control (input,
 * select, textarea, slider). A control that passes the pedals through owns everything but PageUp/PageDown.
 */
export const isKeyOwnedByTarget = (event: Pick<KeyboardEvent, 'key' | 'target'>): boolean => {
  const { target } = event
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (target.isContentEditable) {
    return true
  }
  if (!target.matches(formControlSelector)) {
    return false
  }
  return !(target.hasAttribute(pedalPassThroughAttribute) && (PEDAL_KEYS as readonly string[]).includes(event.key))
}

export interface PageTurner {
  /** The view container */
  container?: HTMLElement | null
  /** Function to call when the user request the previous entity */
  onPrevious?(): void
  /** Function to call when the user request the next entity */
  onNext(): void
}

/** Page turner hook */
export const usePageTurner = ({ container, onPrevious = () => {}, onNext = () => {} }: PageTurner) => {
  const c = container ?? document.body

  const { isAtBeginning, isAtEnd, pageUp, pageDown } = usePageControl(c)

  const handlePrev = useCallback(() => {
    if (isAtBeginning) {
      onPrevious()
    } else {
      pageUp()
    }
  }, [isAtBeginning, onPrevious, pageUp])

  const handleNext = useCallback(() => {
    if (isAtEnd) {
      onNext()
    } else {
      pageDown()
    }
  }, [isAtEnd, onNext, pageDown])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      /**
       * Ignore auto-repeat keydowns. A page-turner pedal (or a physically held
       * key) emits a stream of `repeat: true` keydowns; page-turning and
       * song-advancing are discrete actions that must fire once per physical
       * press. Without this guard a single held press fired `onNext` /
       * `pageDown` dozens of times per second, and on a slower tablet that
       * rapid-fire saturated the main thread mid song-switch and hung the tab
       * (GH #1628). The on-screen controls don't auto-repeat, which is why the
       * hang only reproduced via the page turner.
       */
      if (e.repeat) {
        return
      }
      /** Editable text and focused form controls keep their keys; the pedals still turn past a seek slider */
      if (isKeyOwnedByTarget(e)) {
        return
      }
      if ((PAGE_TURN_BACKWARD_KEYS as readonly string[]).includes(e.key)) {
        handlePrev()
      } else if ((PAGE_TURN_FORWARD_KEYS as readonly string[]).includes(e.key)) {
        handleNext()
      }
    },
    [handleNext, handlePrev]
  )

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown, c])

  return { onKeyDown }
}
